import { useEffect, useMemo, useRef, useState } from 'react';
import type { DragEvent as ReactDragEvent } from 'react';
import {
  AlertTriangle,
  CheckCircle2,
  CheckSquare,
  Clock,
  Copy,
  Crop,
  Eye,
  GripVertical,
  Inbox,
  Loader2,
  RotateCw,
  Search,
  Trash2,
  X,
} from 'lucide-react';

import { CATEGORY_CODES, CATEGORY_LABEL_KEYS } from '../lib/categories';
import { DEBUG } from '../lib/debug';
import { isLowQualityPhoto } from '../lib/findings';
import { t, tn, type StringKey, type UiLangCode } from '../lib/i18n';
import type { CategoryCode, ReceiptSession, Receipt } from '../lib/types';
import { useAppStore } from '../store';
import { DebugPanel } from './DebugPanel';
import { ImagePreviewModal } from './ImagePreviewModal';
import { OcrDebugPanel } from './OcrDebugPanel';
import { ReceiptTable } from './ReceiptTable';
import { TagsNotes } from './TagsNotes';
import { ValidationHints } from './ValidationHints';

interface SessionsListProps {
  sessions: ReceiptSession[];
  lang: UiLangCode;
  onEditReceipt: (id: string, receipt: Receipt) => void;
  onRemoveSession: (id: string) => void;
  /** Drop every session in a terminal (success | error) state. */
  onRemoveDone: () => void;
  /** Drop every session and cancel any in-flight work. */
  onClearAll: () => void;
  /** First-run onboarding: synth a demo receipt and feed it through the pipeline. */
  onTrySample: () => void;
  /** Open the manual-crop modal for the given session. */
  onManualCrop: (id: string) => void;
  /** Set / clear the category on a session. undefined = uncategorise. */
  onSetCategory: (id: string, category: CategoryCode | undefined) => void;
  /** Rotate a session's image 90° clockwise and reprocess. */
  onRotate: (id: string) => void;
  /** Move a session from one batch position to another. */
  onReorder: (fromIdx: number, toIdx: number) => void;
}

const STAGE_KEYS: Record<string, StringKey> = {
  load: 'stage.load',
  preprocess: 'stage.preprocess',
  ocr: 'stage.ocr',
  parse: 'stage.parse',
  validate: 'stage.validate',
  done: 'stage.done',
};

function StateBadge({
  session,
  lang,
}: {
  session: ReceiptSession;
  lang: UiLangCode;
}) {
  const s = session.state;
  switch (s.kind) {
    case 'queued':
      return (
        <span className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2 py-0.5 text-xs font-medium text-slate-700 dark:bg-slate-800 dark:text-slate-300">
          <Clock className="h-3 w-3" aria-hidden="true" />
          {t(lang, 'state.queued')}
        </span>
      );
    case 'processing': {
      const stageKey = STAGE_KEYS[s.stage];
      const stageLabel = stageKey !== undefined ? t(lang, stageKey) : s.stage;
      return (
        <span
          role="status"
          aria-live="polite"
          className="inline-flex items-center gap-1 rounded-full bg-indigo-50 px-2 py-0.5 text-xs font-medium text-indigo-700 ring-1 ring-inset ring-indigo-600/20 dark:bg-indigo-500/10 dark:text-indigo-300 dark:ring-indigo-500/30"
        >
          <Loader2 className="h-3 w-3 animate-spin" aria-hidden="true" />
          {stageLabel}
          {s.progress !== undefined && ` ${Math.round(s.progress * 100)}%`}
        </span>
      );
    }
    case 'success':
      if (isLowQualityPhoto(session)) {
        return (
          <span
            role="status"
            className="inline-flex items-center gap-1 rounded-full bg-amber-50 px-2 py-0.5 text-xs font-medium text-amber-800 ring-1 ring-inset ring-amber-600/20 dark:bg-amber-500/10 dark:text-amber-300 dark:ring-amber-500/30"
          >
            <AlertTriangle className="h-3 w-3" aria-hidden="true" />
            {t(lang, 'state.lowQuality')}
          </span>
        );
      }
      return (
        <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2 py-0.5 text-xs font-medium text-emerald-700 ring-1 ring-inset ring-emerald-600/20 dark:bg-emerald-500/10 dark:text-emerald-300 dark:ring-emerald-500/30">
          <CheckCircle2 className="h-3 w-3" aria-hidden="true" />
          {t(lang, 'state.done')}
        </span>
      );
    case 'error':
      return (
        <span
          role="alert"
          className="inline-flex items-center gap-1 rounded-full bg-red-50 px-2 py-0.5 text-xs font-medium text-red-700 ring-1 ring-inset ring-red-600/20 dark:bg-red-500/10 dark:text-red-300 dark:ring-red-500/30"
        >
          <AlertTriangle className="h-3 w-3" aria-hidden="true" />
          {t(lang, 'state.error')}
        </span>
      );
  }
}

/**
 * Walk the batch and return the set of session ids whose receipt matches
 * AT LEAST ONE other session on the (vendor, date, total) tuple. Tuple
 * choice is deliberate — it catches "I dropped the same receipt twice"
 * (the dominant duplicate failure mode) without flagging legitimate
 * same-day same-vendor visits where the totals differ. Only success-
 * state sessions with a receipt participate; in-flight ones can't be
 * checked yet. Vendor compared case-insensitively after trim — OCR
 * sometimes capitalises differently between two scans of the same paper.
 */
function findDuplicateIds(sessions: readonly ReceiptSession[]): Set<string> {
  const groups = new Map<string, string[]>();
  for (const s of sessions) {
    if (s.state.kind !== 'success' || s.receipt === null) continue;
    const r = s.receipt;
    const day = r.date.value.toISOString().slice(0, 10);
    const vendor = r.vendor.value.trim().toLowerCase();
    const key = `${vendor}|${day}|${r.total.value}`;
    const list = groups.get(key) ?? [];
    list.push(s.id);
    groups.set(key, list);
  }
  const dupes = new Set<string>();
  for (const ids of groups.values()) {
    if (ids.length > 1) for (const id of ids) dupes.add(id);
  }
  return dupes;
}

/**
 * Case-insensitive substring search across the fields a user is most
 * likely to recognise: filename, vendor name, line-item names, and
 * any user-supplied tags / notes. Empty query matches everything;
 * in-flight sessions (no receipt yet) still match by filename.
 */
function matchesSearch(session: ReceiptSession, q: string): boolean {
  if (q === '') return true;
  if (session.filename.toLowerCase().includes(q)) return true;
  const r = session.receipt;
  if (r === null) return false;
  if (r.vendor.value.toLowerCase().includes(q)) return true;
  for (const item of r.items) {
    if (item.name.value.toLowerCase().includes(q)) return true;
  }
  if (r.tags !== undefined) {
    for (const tag of r.tags) {
      if (tag.toLowerCase().includes(q)) return true;
    }
  }
  if (r.notes !== undefined && r.notes.toLowerCase().includes(q)) return true;
  return false;
}

function summaryText(session: ReceiptSession): string | null {
  const r = session.receipt;
  if (r === null) return null;
  const parts: string[] = [];
  if (r.vendor.value) parts.push(r.vendor.value);
  if (Number.isFinite(r.total.value) && r.total.value > 0) {
    parts.push(`${(r.total.value / 100).toFixed(2)} ${r.currency.value}`);
  }
  return parts.length > 0 ? parts.join(' · ') : null;
}

function CategoryRow({
  sessionId,
  category,
  categoryScore,
  lang,
  onSetCategory,
}: {
  sessionId: string;
  category: CategoryCode | undefined;
  categoryScore: number | undefined;
  lang: UiLangCode;
  onSetCategory: (id: string, category: CategoryCode | undefined) => void;
}) {
  const selectId = `category-${sessionId}`;
  const pct =
    categoryScore !== undefined ? Math.round(categoryScore * 100) : undefined;
  return (
    <div className="flex flex-wrap items-center gap-2">
      <label
        htmlFor={selectId}
        className="text-xs font-medium uppercase tracking-wide text-slate-500 dark:text-slate-400"
      >
        {t(lang, 'category.label')}
      </label>
      <select
        id={selectId}
        value={category ?? ''}
        onChange={(e) => {
          const v = e.target.value;
          onSetCategory(sessionId, v === '' ? undefined : (v as CategoryCode));
        }}
        className="rounded-md border border-slate-300 bg-white px-2 py-1 text-sm text-slate-900 transition-colors focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100 dark:focus:border-indigo-400 dark:focus:ring-indigo-400"
      >
        <option value="">{t(lang, 'category.placeholder')}</option>
        {CATEGORY_CODES.map((code) => (
          <option key={code} value={code}>
            {t(lang, CATEGORY_LABEL_KEYS[code])}
          </option>
        ))}
      </select>
      {pct !== undefined && category !== undefined && (
        <span
          role="status"
          title={t(lang, 'category.autoTooltip', { pct })}
          className="inline-flex items-center rounded-full bg-amber-50 px-2 py-0.5 text-xs font-medium text-amber-800 ring-1 ring-inset ring-amber-600/20 dark:bg-amber-500/10 dark:text-amber-300 dark:ring-amber-500/30"
        >
          {t(lang, 'category.autoBadge', { pct })}
        </span>
      )}
    </div>
  );
}

export function SessionsList({
  sessions,
  lang,
  onEditReceipt,
  onRemoveSession,
  onRemoveDone,
  onClearAll,
  onTrySample,
  onManualCrop,
  onSetCategory,
  onRotate,
  onReorder,
}: SessionsListProps) {
  const showDebug = useAppStore((s) => s.showDebug);
  const duplicateIds = findDuplicateIds(sessions);

  // Search query — local to the component because it's an ephemeral
  // find-mode, not part of the persisted batch state.
  const [searchQuery, setSearchQuery] = useState('');
  const normalizedQuery = searchQuery.trim().toLowerCase();
  const isFiltering = normalizedQuery !== '';
  const visibleSessions = useMemo(
    () =>
      isFiltering
        ? sessions.filter((s) => matchesSearch(s, normalizedQuery))
        : sessions,
    [sessions, normalizedQuery, isFiltering],
  );

  // Image-preview modal state. Tracks the session id whose post-scan image
  // is being shown in the lightbox; null = no preview open.
  const [previewSessionId, setPreviewSessionId] = useState<string | null>(null);

  // Drag-and-drop reorder state — same pattern as ReceiptTable's line-item
  // drag. draggedIdx: which row started; dragOverIdx: where the cursor
  // currently is (drives the insertion-line indicator). dragArmedIdRef
  // gates dragstart to the grip handle so clicking inputs / the X
  // button doesn't accidentally start a drag.
  const [draggedIdx, setDraggedIdx] = useState<number | null>(null);
  const [dragOverIdx, setDragOverIdx] = useState<number | null>(null);
  const dragArmedIdRef = useRef<string | null>(null);

  useEffect(() => {
    const onWindowMouseUp = () => {
      dragArmedIdRef.current = null;
    };
    window.addEventListener('mouseup', onWindowMouseUp);
    return () => window.removeEventListener('mouseup', onWindowMouseUp);
  }, []);

  const handleDragStart = (
    e: ReactDragEvent<HTMLElement>,
    idx: number,
    id: string,
  ): void => {
    // If the drag started inside a nested draggable element — e.g. a
    // line-item row in the embedded ReceiptTable — the dragstart event
    // bubbles to us, but that inner element is the real source. Don't
    // intercept (and especially don't preventDefault, which would
    // cancel the inner drag entirely).
    let walk: HTMLElement | null = e.target as HTMLElement;
    while (walk !== null && walk !== e.currentTarget) {
      if (walk.draggable) return;
      walk = walk.parentElement;
    }
    if (dragArmedIdRef.current !== id) {
      e.preventDefault();
      return;
    }
    setDraggedIdx(idx);
    e.dataTransfer.effectAllowed = 'move';
    // Some browsers cancel the drag if no data is set.
    e.dataTransfer.setData('text/plain', String(idx));
  };

  const handleDragOver = (e: ReactDragEvent<HTMLElement>, idx: number): void => {
    if (draggedIdx === null) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (dragOverIdx !== idx) setDragOverIdx(idx);
  };

  const handleDrop = (
    e: ReactDragEvent<HTMLElement>,
    targetIdx: number,
  ): void => {
    e.preventDefault();
    const from = draggedIdx;
    setDraggedIdx(null);
    setDragOverIdx(null);
    dragArmedIdRef.current = null;
    if (from === null || from === targetIdx) return;
    // Mirror ReceiptTable's index-shift correction: after splicing the
    // moved row out, indices to its right shift left by 1.
    const insertAt = from < targetIdx ? targetIdx - 1 : targetIdx;
    onReorder(from, insertAt);
  };

  const handleDragEnd = (): void => {
    setDraggedIdx(null);
    setDragOverIdx(null);
    dragArmedIdRef.current = null;
  };
  if (sessions.length === 0) {
    return (
      <div className="flex flex-col items-center gap-2 rounded-xl border border-dashed border-slate-200 bg-white px-6 py-12 text-center dark:border-slate-700 dark:bg-slate-900/50">
        <Inbox className="h-8 w-8 text-slate-300 dark:text-slate-600" aria-hidden="true" />
        <p className="text-sm text-slate-500 dark:text-slate-400">
          {t(lang, 'sessions.empty')}
        </p>
        <button
          type="button"
          onClick={onTrySample}
          className="mt-2 inline-flex items-center rounded-md px-3 py-2 text-sm font-medium text-indigo-600 underline-offset-4 transition-colors hover:text-indigo-700 hover:underline focus:outline-none focus:underline dark:text-indigo-400 dark:hover:text-indigo-300"
        >
          {t(lang, 'sessions.trySample')}
        </button>
      </div>
    );
  }

  const doneCount = sessions.filter(
    (s) => s.state.kind === 'success' || s.state.kind === 'error',
  ).length;
  const hasInFlight = sessions.some(
    (s) => s.state.kind === 'queued' || s.state.kind === 'processing',
  );

  const countLabel = isFiltering
    ? tn(
        lang,
        visibleSessions.length,
        'sessions.count.partial.one',
        'sessions.count.partial.many',
        { total: sessions.length },
      )
    : tn(
        lang,
        sessions.length,
        'sessions.count.one',
        'sessions.count.many',
      );

  return (
    <div className="space-y-3">
      {sessions.length >= 2 && (
        <div className="relative">
          <Search
            className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400 dark:text-slate-500"
            aria-hidden="true"
          />
          <input
            type="search"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder={t(lang, 'sessions.search.placeholder')}
            aria-label={t(lang, 'sessions.search.placeholder')}
            className="w-full rounded-lg border border-slate-200 bg-white py-2 pl-9 pr-3 text-sm text-slate-900 placeholder-slate-400 transition-colors focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100 dark:placeholder-slate-500 dark:focus:border-indigo-400 dark:focus:ring-indigo-400"
          />
        </div>
      )}
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-xs font-medium uppercase tracking-wide text-slate-500 dark:text-slate-400">
          {countLabel}
        </p>
        <div className="flex flex-wrap items-center gap-1">
          {doneCount > 0 && (
            <button
              type="button"
              onClick={onRemoveDone}
              className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs font-medium text-slate-600 transition-colors hover:bg-slate-100 hover:text-slate-900 dark:text-slate-400 dark:hover:bg-slate-800 dark:hover:text-slate-100"
              title={t(lang, 'sessions.clearDoneTooltip')}
            >
              <CheckSquare className="h-3.5 w-3.5" aria-hidden="true" />
              {t(lang, 'sessions.clearDone', { n: doneCount })}
            </button>
          )}
          <button
            type="button"
            onClick={onClearAll}
            className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs font-medium text-slate-600 transition-colors hover:bg-red-50 hover:text-red-700 dark:text-slate-400 dark:hover:bg-red-500/10 dark:hover:text-red-400"
            title={
              hasInFlight
                ? t(lang, 'sessions.clearAllTooltip.inFlight')
                : t(lang, 'sessions.clearAllTooltip.idle')
            }
          >
            <Trash2 className="h-3.5 w-3.5" aria-hidden="true" />
            {t(lang, 'sessions.clearAll')}
          </button>
        </div>
      </div>
      {isFiltering && visibleSessions.length === 0 && (
        <div className="rounded-xl border border-dashed border-slate-200 bg-white px-6 py-8 text-center dark:border-slate-700 dark:bg-slate-900/50">
          <p className="text-sm text-slate-500 dark:text-slate-400">
            {t(lang, 'sessions.search.empty')}
          </p>
        </div>
      )}
      {visibleSessions.map((session, idx) => {
        const summary = summaryText(session);
        const errorMsg = session.state.kind === 'error' ? session.state.error : null;
        // Auto-open processing/error sessions so the user sees activity
        // without having to click. Done sessions stay collapsed by default
        // (the header summary is enough at a glance).
        const autoOpen =
          session.state.kind === 'processing' || session.state.kind === 'error';
        const isDragged = draggedIdx === idx;
        const isDropTarget =
          draggedIdx !== null && dragOverIdx === idx && draggedIdx !== idx;

        return (
          <details
            key={session.id}
            draggable
            onDragStart={(e) => handleDragStart(e, idx, session.id)}
            onDragOver={(e) => handleDragOver(e, idx)}
            onDrop={(e) => handleDrop(e, idx)}
            onDragEnd={handleDragEnd}
            className={`group rounded-xl border border-slate-200 bg-white shadow-sm transition-all open:shadow-md dark:border-slate-800 dark:bg-slate-900 ${
              isDragged ? 'opacity-40' : ''
            } ${
              isDropTarget
                ? 'border-t-2 border-t-indigo-500 dark:border-t-indigo-400'
                : ''
            }`}
            {...(autoOpen ? { open: true } : {})}
          >
            <summary className="group/row flex cursor-pointer select-none items-center gap-3 px-4 py-3 hover:bg-slate-50 dark:hover:bg-slate-800/50">
              <span
                onMouseDown={(e) => {
                  // Reordering during a search filter would mis-target —
                  // the visible indices don't map 1:1 to the full sessions
                  // array. Skip arming so dragstart bails out, and dim the
                  // handle visually as a hint.
                  if (isFiltering) return;
                  e.stopPropagation();
                  dragArmedIdRef.current = session.id;
                }}
                onClick={(e) => {
                  // Clicking the grip without dragging would otherwise toggle
                  // the details. Block that.
                  e.preventDefault();
                  e.stopPropagation();
                }}
                aria-label={t(lang, 'sessions.dragHandleAria', {
                  filename: session.filename,
                })}
                title={t(lang, 'sessions.dragHandle')}
                className={`-ml-1 transition-colors ${
                  isFiltering
                    ? 'cursor-not-allowed text-slate-200 dark:text-slate-700'
                    : 'cursor-grab text-slate-300 hover:text-slate-600 active:cursor-grabbing dark:text-slate-600 dark:hover:text-slate-300'
                }`}
              >
                <GripVertical className="h-4 w-4" aria-hidden="true" />
              </span>
              <span className="flex min-w-0 flex-1 items-center gap-1.5">
                <span className="truncate text-sm font-medium text-slate-800 dark:text-slate-100">
                  {session.filename}
                </span>
                {session.state.kind === 'success' &&
                  (session.correctedImage !== null || session.originalFile !== null) && (
                    <button
                      type="button"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        setPreviewSessionId(session.id);
                      }}
                      aria-label={t(lang, 'sessions.previewAria', {
                        filename: session.filename,
                      })}
                      title={t(lang, 'sessions.previewTooltip')}
                      className="inline-flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-md text-slate-400 opacity-0 transition-opacity hover:bg-slate-100 hover:text-slate-700 focus-visible:opacity-100 group-hover/row:opacity-100 dark:text-slate-500 dark:hover:bg-slate-800 dark:hover:text-slate-200"
                    >
                      <Eye className="h-4 w-4" aria-hidden="true" />
                    </button>
                  )}
              </span>
              {summary !== null && (
                <span className="hidden text-xs text-slate-500 dark:text-slate-400 sm:inline">
                  {summary}
                </span>
              )}
              <StateBadge session={session} lang={lang} />
              {duplicateIds.has(session.id) && (
                <span
                  role="status"
                  title={t(lang, 'sessions.duplicateTooltip')}
                  className="inline-flex items-center gap-1 rounded-full bg-amber-50 px-2 py-0.5 text-xs font-medium text-amber-800 ring-1 ring-inset ring-amber-600/20 dark:bg-amber-500/10 dark:text-amber-300 dark:ring-amber-500/30"
                >
                  <Copy className="h-3 w-3" aria-hidden="true" />
                  <span className="hidden sm:inline">
                    {t(lang, 'sessions.duplicate')}
                  </span>
                </span>
              )}
              <button
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  onRemoveSession(session.id);
                }}
                aria-label={t(lang, 'sessions.removeAriaLabel', {
                  filename: session.filename,
                })}
                className="ml-1 inline-flex h-9 w-9 items-center justify-center rounded-md text-slate-400 transition-colors hover:bg-red-50 hover:text-red-700 dark:text-slate-500 dark:hover:bg-red-500/10 dark:hover:text-red-400"
                title={t(lang, 'sessions.removeFromList')}
              >
                <X className="h-4 w-4" aria-hidden="true" />
              </button>
            </summary>
            <div className="space-y-3 border-t border-slate-200 px-4 py-3 dark:border-slate-800">
              {session.state.kind === 'processing' && session.state.message !== undefined && (
                <p className="font-mono text-xs text-slate-500 dark:text-slate-400">
                  {session.state.message}
                </p>
              )}
              {errorMsg !== null && (
                <p className="text-sm text-red-700 dark:text-red-400">{errorMsg}</p>
              )}
              {isLowQualityPhoto(session) && (
                <div
                  role="alert"
                  className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900 dark:border-amber-500/30 dark:bg-amber-500/10 dark:text-amber-200"
                >
                  <AlertTriangle
                    className="mt-0.5 h-4 w-4 flex-shrink-0"
                    aria-hidden="true"
                  />
                  <div className="space-y-1">
                    <p className="font-medium">{t(lang, 'sessions.lowQuality.title')}</p>
                    <p className="text-xs leading-relaxed">
                      {t(lang, 'sessions.lowQuality.body')}
                    </p>
                  </div>
                </div>
              )}
              {(session.state.kind === 'success' || session.state.kind === 'error') &&
                session.originalFile !== null && (
                  <div className="flex flex-wrap items-center gap-2">
                    <button
                      type="button"
                      onClick={() => onManualCrop(session.id)}
                      className="inline-flex items-center gap-1.5 rounded-md border border-slate-300 bg-white px-3 py-1.5 text-xs font-medium text-slate-700 transition-colors hover:bg-slate-50 hover:text-slate-900 focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-300 dark:hover:bg-slate-800 dark:hover:text-slate-100"
                      title={t(lang, 'sessions.recropTooltip')}
                    >
                      <Crop className="h-3.5 w-3.5" aria-hidden="true" />
                      {t(lang, 'sessions.recropManually')}
                    </button>
                    {session.state.kind === 'success' &&
                      session.correctedImage !== null && (
                        <button
                          type="button"
                          onClick={() => onRotate(session.id)}
                          className="inline-flex items-center gap-1.5 rounded-md border border-slate-300 bg-white px-3 py-1.5 text-xs font-medium text-slate-700 transition-colors hover:bg-slate-50 hover:text-slate-900 focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-300 dark:hover:bg-slate-800 dark:hover:text-slate-100"
                          title={t(lang, 'sessions.rotateTooltip')}
                        >
                          <RotateCw className="h-3.5 w-3.5" aria-hidden="true" />
                          {t(lang, 'sessions.rotate')}
                        </button>
                      )}
                  </div>
                )}
              {DEBUG && showDebug && (
                <DebugPanel
                  entries={session.debugLog}
                  correctedImage={session.correctedImage}
                  filename={session.filename}
                />
              )}
              {session.state.kind === 'success' && session.receipt !== null && (
                <CategoryRow
                  sessionId={session.id}
                  category={session.receipt.category}
                  categoryScore={session.receipt.categoryScore}
                  lang={lang}
                  onSetCategory={onSetCategory}
                />
              )}
              {session.state.kind === 'success' && session.receipt !== null && (
                <TagsNotes
                  receipt={session.receipt}
                  lang={lang}
                  onEdit={(updated) => onEditReceipt(session.id, updated)}
                />
              )}
              {session.receipt !== null && (
                <ValidationHints receipt={session.receipt} lang={lang} />
              )}
              {session.receipt !== null && (
                <ReceiptTable
                  receipt={session.receipt}
                  lang={lang}
                  onEdit={(updated) => onEditReceipt(session.id, updated)}
                />
              )}
              {DEBUG && showDebug && session.ocrDebug !== null && (
                <OcrDebugPanel ocr={session.ocrDebug} />
              )}
            </div>
          </details>
        );
      })}
      {/*
       * End-of-list drop zone. The per-row drop indicator places the
       * dragged session BEFORE its target — without this trailing zone
       * there's no way to drop at the very end of the list. Only
       * rendered while a drag is in flight so it doesn't take vertical
       * space at rest.
       */}
      {draggedIdx !== null && (
        <div
          onDragOver={(e) => handleDragOver(e, sessions.length)}
          onDrop={(e) => handleDrop(e, sessions.length)}
          aria-hidden="true"
          className={`h-6 rounded-md border-2 border-dashed transition-colors ${
            dragOverIdx === sessions.length
              ? 'border-indigo-500 bg-indigo-50/50 dark:border-indigo-400 dark:bg-indigo-500/10'
              : 'border-transparent'
          }`}
        />
      )}
      {(() => {
        if (previewSessionId === null) return null;
        const s = sessions.find((x) => x.id === previewSessionId);
        if (s === undefined) return null;
        const blob = s.correctedImage?.blob ?? s.originalFile;
        if (blob === null || blob === undefined) return null;
        return (
          <ImagePreviewModal
            source={blob}
            filename={s.filename}
            onClose={() => setPreviewSessionId(null)}
          />
        );
      })()}
    </div>
  );
}
